import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Users, 
  Calendar,
  Clock,
  ChevronLeft,
  CheckSquare
} from 'lucide-react';
import { Doctor, Staff, Room, ScheduleItem } from './SchedulerTypes';
import { getDoctorPreferences, DoctorStaffPreferenceWithDetails } from '@/services/adminService';
import { isDAAvailable, isDAOnLeave } from '@/services/backupScheduleService';
import { formatDateToString } from '@/lib/dateUtils';

export interface AffectedSchedule {
  scheduleId: string;
  date: string;
  startTime: string;
  endTime: string;
  doctorId: string;
  doctorName: string;
  roomId?: string;
  roomName?: string;
  title: string;
  type: string;
}

export interface Reassignment {
  scheduleId: string;
  originalStaffId: string;
  originalStaffName: string;
  newStaffId: string;
  newStaffName?: string;
  preferenceRank: number;
  doctorId: string;
}

interface ConflictResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffId: string;
  staffName: string;
  leaveType: 'leave' | 'mc';
  startDate: Date;
  endDate?: Date;
  affectedSchedules: AffectedSchedule[];
  scheduleItems: ScheduleItem[];
  onConfirmReassignments: (reassignments: Reassignment[]) => void;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
}

interface BackupSuggestion {
  staffId: string;
  staffName: string;
  preferenceRank: number;
  isAvailable: boolean;
  conflictReason?: string;
}

interface ScheduleWithBackup {
  schedule: AffectedSchedule;
  suggestions: BackupSuggestion[];
  selectedBackup?: string;
  isConfirmed: boolean;
}

const ConflictResolutionModal: React.FC<ConflictResolutionModalProps> = ({
  isOpen,
  onClose,
  staffId,
  staffName,
  leaveType,
  startDate,
  endDate,
  affectedSchedules,
  scheduleItems,
  onConfirmReassignments,
  doctors,
  staff,
  rooms
}) => {
  const [schedulesWithBackup, setSchedulesWithBackup] = useState<ScheduleWithBackup[]>([]);
  const [loading, setLoading] = useState(false);
  const [allConfirmed, setAllConfirmed] = useState(false);

  // Load backup suggestions when modal opens
  useEffect(() => {
    if (isOpen && affectedSchedules.length > 0) {
      loadBackupSuggestions();
    }
  }, [isOpen, affectedSchedules]);

  const loadBackupSuggestions = async () => {
    setLoading(true);
    try {
      const schedulesWithSuggestions = await Promise.all(
        affectedSchedules.map(async (schedule) => {
          const suggestions = await getBackupSuggestionsForSchedule(schedule);
          
          // Auto-select first available backup
          const firstAvailable = suggestions.find(s => s.isAvailable);
          
          return {
            schedule,
            suggestions,
            selectedBackup: firstAvailable?.staffId,
            isConfirmed: !!firstAvailable
          };
        })
      );
      
      setSchedulesWithBackup(schedulesWithSuggestions);
    } catch (error) {
      console.error('Error loading backup suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBackupSuggestionsForSchedule = async (
    schedule: AffectedSchedule
  ): Promise<BackupSuggestion[]> => {
    try {
      // Get doctor's preferences
      const preferences = await getDoctorPreferences(schedule.doctorId);
      
      // Get dental assistants
      const dentalAssistants = staff.filter(s => 
        s.role.toLowerCase().includes('dental assistant') && 
        s.status === 'active' &&
        s.id !== staffId // Exclude the staff member going on leave
      );
      
      // Map preferences to suggestions
      const suggestions: BackupSuggestion[] = [];
      
      for (const pref of preferences) {
        const staffMember = dentalAssistants.find(s => s.id === pref.staff_id);
        
        if (staffMember) {
          // Check if DA is available
          const isOnLeave = isDAOnLeave(staffMember.id, schedule.date, scheduleItems);
          const hasConflict = !isDAAvailable(
            staffMember.id, 
            schedule.startTime, 
            schedule.date, 
            scheduleItems
          );
          
          suggestions.push({
            staffId: staffMember.id,
            staffName: staffMember.short_name || staffMember.name,
            preferenceRank: pref.preference_rank,
            isAvailable: !isOnLeave && !hasConflict,
            conflictReason: isOnLeave ? 'On Leave' : hasConflict ? 'Already Assigned' : undefined
          });
        }
      }
      
      // Add other available DAs not in preferences
      const preferredIds = preferences.map(p => p.staff_id);
      const otherAvailableDAs = dentalAssistants.filter(da => 
        !preferredIds.includes(da.id) &&
        !isDAOnLeave(da.id, schedule.date, scheduleItems) &&
        isDAAvailable(da.id, schedule.startTime, schedule.date, scheduleItems)
      );
      
      otherAvailableDAs.forEach(da => {
        suggestions.push({
          staffId: da.id,
          staffName: da.short_name || da.name,
          preferenceRank: 5, // Not in preferences
          isAvailable: true
        });
      });
      
      // Sort by preference rank and availability
      suggestions.sort((a, b) => {
        if (a.isAvailable !== b.isAvailable) {
          return a.isAvailable ? -1 : 1;
        }
        return a.preferenceRank - b.preferenceRank;
      });
      
      return suggestions;
    } catch (error) {
      console.error('Error getting backup suggestions:', error);
      return [];
    }
  };

  const handleBackupSelection = (scheduleId: string, newStaffId: string) => {
    setSchedulesWithBackup(prev => prev.map(item => {
      if (item.schedule.scheduleId === scheduleId) {
        return {
          ...item,
          selectedBackup: newStaffId,
          isConfirmed: true
        };
      }
      return item;
    }));
  };

  const handleConfirmToggle = (scheduleId: string, checked: boolean) => {
    setSchedulesWithBackup(prev => prev.map(item => {
      if (item.schedule.scheduleId === scheduleId) {
        return {
          ...item,
          isConfirmed: checked
        };
      }
      return item;
    }));
  };

  const handleConfirmAll = () => {
    // Auto-select first available backup for all unconfirmed schedules
    setSchedulesWithBackup(prev => prev.map(item => {
      if (!item.isConfirmed && item.suggestions.length > 0) {
        const firstAvailable = item.suggestions.find(s => s.isAvailable);
        if (firstAvailable) {
          return {
            ...item,
            selectedBackup: firstAvailable.staffId,
            isConfirmed: true
          };
        }
      }
      return {
        ...item,
        isConfirmed: true
      };
    }));
    setAllConfirmed(true);
  };

  const handleApplyReassignments = () => {
    const reassignments: Reassignment[] = [];
    
    schedulesWithBackup.forEach(item => {
      if (item.isConfirmed && item.selectedBackup) {
        const suggestion = item.suggestions.find(s => s.staffId === item.selectedBackup);
        reassignments.push({
          scheduleId: item.schedule.scheduleId,
          originalStaffId: staffId,
          originalStaffName: staffName,
          newStaffId: item.selectedBackup,
          newStaffName: suggestion?.staffName,
          preferenceRank: suggestion?.preferenceRank || 5,
          doctorId: item.schedule.doctorId
        });
      }
    });
    
    onConfirmReassignments(reassignments);
    onClose();
  };

  const getPreferenceRankBadge = (rank: number) => {
    const labels = {
      1: 'Main Nurse',
      2: 'Primary Backup',
      3: 'Secondary Backup',
      4: 'Third Backup',
      5: 'Other Available'
    };
    
    const colors = {
      1: 'bg-green-600 text-white',
      2: 'bg-blue-500 text-white',
      3: 'bg-yellow-500 text-white',
      4: 'bg-orange-500 text-white',
      5: 'bg-gray-500 text-white'
    };
    
    return (
      <Badge className={colors[rank as keyof typeof colors] || colors[5]}>
        {labels[rank as keyof typeof labels] || 'Other'}
      </Badge>
    );
  };

  const totalAffected = affectedSchedules.length;
  const totalResolved = schedulesWithBackup.filter(s => s.isConfirmed && s.selectedBackup).length;
  const totalUnresolved = totalAffected - totalResolved;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <span>Schedule Conflicts Detected</span>
          </DialogTitle>
          <DialogDescription>
            {staffName} is taking {leaveType === 'mc' ? 'urgent leave' : 'leave'} from{' '}
            {formatDateToString(startDate)}
            {endDate && ` to ${formatDateToString(endDate)}`}.
            {totalAffected} schedule{totalAffected !== 1 ? 's' : ''} will be affected.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading backup suggestions...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Alert */}
            <Alert>
              <Users className="h-4 w-4" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>
                    <strong>{totalAffected}</strong> affected schedule{totalAffected !== 1 ? 's' : ''} found
                  </span>
                  <div className="flex gap-4">
                    <span className="text-green-600">
                      <CheckCircle2 className="inline h-4 w-4 mr-1" />
                      {totalResolved} resolved
                    </span>
                    {totalUnresolved > 0 && (
                      <span className="text-orange-600">
                        <XCircle className="inline h-4 w-4 mr-1" />
                        {totalUnresolved} pending
                      </span>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {/* Affected Schedules */}
            <div className="space-y-3">
              {schedulesWithBackup.map((item, index) => {
                const doctor = doctors.find(d => d.id === item.schedule.doctorId);
                const room = rooms.find(r => r.id === item.schedule.roomId);
                
                return (
                  <Card key={item.schedule.scheduleId} className={item.isConfirmed ? 'border-green-500' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {item.schedule.date}
                          <Clock className="h-4 w-4 ml-2" />
                          {item.schedule.startTime} - {item.schedule.endTime}
                        </CardTitle>
                        <Checkbox
                          checked={item.isConfirmed}
                          onCheckedChange={(checked) => 
                            handleConfirmToggle(item.schedule.scheduleId, checked as boolean)
                          }
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Doctor:</span>
                          <span className="ml-2 font-medium">
                            Dr {doctor?.short_name || doctor?.name}
                          </span>
                        </div>
                        {room && (
                          <div>
                            <span className="text-muted-foreground">Room:</span>
                            <span className="ml-2 font-medium">{room.name}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <div className="text-sm text-muted-foreground">Backup DA Assignment:</div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={item.selectedBackup || ''}
                            onValueChange={(value) => handleBackupSelection(item.schedule.scheduleId, value)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select backup DA..." />
                            </SelectTrigger>
                            <SelectContent>
                              {item.suggestions.length === 0 ? (
                                <SelectItem value="none" disabled>
                                  No backup available
                                </SelectItem>
                              ) : (
                                item.suggestions.map(suggestion => (
                                  <SelectItem
                                    key={suggestion.staffId}
                                    value={suggestion.staffId}
                                    disabled={!suggestion.isAvailable}
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span className={!suggestion.isAvailable ? 'line-through' : ''}>
                                        {suggestion.staffName}
                                      </span>
                                      <div className="flex items-center gap-2 ml-2">
                                        {getPreferenceRankBadge(suggestion.preferenceRank)}
                                        {suggestion.conflictReason && (
                                          <span className="text-xs text-muted-foreground">
                                            ({suggestion.conflictReason})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {!item.selectedBackup && item.suggestions.length === 0 && (
                          <Alert className="bg-orange-50">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                              No backup DA available for this time slot. Schedule will remain unassigned.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={handleConfirmAll}
              disabled={allConfirmed || schedulesWithBackup.length === 0}
              className="flex-1 sm:flex-initial"
            >
              <CheckSquare className="mr-2 h-4 w-4" />
              Confirm All
            </Button>
            
            <Button
              onClick={handleApplyReassignments}
              disabled={totalResolved === 0}
              className="flex-1 sm:flex-initial"
            >
              Apply Reassignments ({totalResolved})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictResolutionModal;
